import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import type { MoodboardItem, MoodboardPolarity } from '@backend/lib/db/schema/moodboard'
import type { Color as ColorValue } from '@backend/lib/db/typeRoots/color'
import { Color, colorToHex, hexToColor } from '@/components/modules/CanvasEditor/color'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { useCanvasStore } from '../canvas-store'
import { useDragInteraction } from '../use-drag-interaction'
import { SNAP_CURVE } from '../motion-tokens'
import { ItemInfoBadge } from './item-info-badge'
import { ResizeHandle } from './resize-handle'

type ColorItem = Extract<MoodboardItem, { type: 'color' }>

type Props = {
  item: ColorItem
  allItems: MoodboardItem[]
  onMove: (position: { x: number; y: number }) => Promise<void> | void
  onResize: (size: { w: number; h: number }) => Promise<void> | void
  onChange: (patch: { hex?: string; label?: string }) => Promise<void> | void
  onMetaChange: (patch: {
    polarity?: MoodboardPolarity | undefined
    annotation?: string | undefined
  }) => Promise<void> | void
}

// User-curated brand color. The whole card is the swatch; the bottom strip overlays
// the hex + optional label as inline-editable controls — clicking the hex opens the
// workspace's Chrome color picker via popover, the label is a transparent input the
// user can type into directly.
export function ColorCard({ item, allItems, onMove, onResize, onChange, onMetaChange }: Props) {
  const override = useCanvasStore((s) => s.dragOverrides[item.id])
  const resizeOverride = useCanvasStore((s) => s.resizeOverrides[item.id])
  const isDragging = !!override
  const position = override?.position ?? item.position
  const size = resizeOverride?.size ?? item.size
  const isSelected = useCanvasStore((s) => s.selectedIds.has(item.id))

  const [labelDraft, setLabelDraft] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerTriggerRef = useRef<HTMLButtonElement>(null)
  const pickerContentRef = useRef<HTMLDivElement>(null)

  // Auto-open the picker on a freshly-placed card so the user can pick a color right away
  // (the toolbar places the card with a default, then hands off to this picker).
  const justPlacedColorId = useCanvasStore((s) => s.justPlacedColorId)
  const setJustPlacedColorId = useCanvasStore((s) => s.setJustPlacedColorId)
  useEffect(() => {
    if (justPlacedColorId !== item.id) return
    setPickerOpen(true)
    setJustPlacedColorId(null)
  }, [justPlacedColorId, item.id, setJustPlacedColorId])

  // Same capture-phase outside-click handling as ItemInfoBadge — card-level
  // pointerdown handlers stopPropagation to prevent canvas marquee, which
  // also blocks Radix's default outside-click listener.
  useEffect(() => {
    if (!pickerOpen) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (pickerTriggerRef.current?.contains(target)) return
      if (pickerContentRef.current?.contains(target)) return
      if (target.closest('[data-radix-popper-content-wrapper]')) return
      setPickerOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [pickerOpen])

  const drag = useDragInteraction({
    itemId: item.id,
    basePosition: item.position,
    linkedItems: allItems,
    onCommit: onMove,
    onSelect: (e) => {
      useCanvasStore.getState().selectFromPointer(item.id, e.shiftKey)
    },
  })

  // Same hover-vs-commit pattern as StickyNote — Chrome fires `isHoverSelection`
  // for every pointer-move on the sliders. Render a local preview during drag,
  // round-trip to tRPC only on commit (mouseup).
  const [previewHex, setPreviewHex] = useState<string | null>(null)
  const effectiveHex = previewHex ?? item.hex

  const onLight = isLightColor(effectiveHex)
  const overlayText = onLight ? 'text-black/80' : 'text-white/90'
  const overlayMuted = onLight ? 'text-black/55' : 'text-white/65'
  const overlayBg = onLight ? 'bg-black/5' : 'bg-white/10'
  const overlayPlaceholder = onLight ? 'placeholder:text-black/35' : 'placeholder:text-white/40'

  const commitLabel = () => {
    if (labelDraft === null) return
    const next = labelDraft.trim()
    setLabelDraft(null)
    if ((next || undefined) !== (item.label || undefined)) {
      void onChange({ label: next })
    }
  }

  // ColorPickerQuickmenu speaks in {r,g,b,a} — convert at the boundary.
  const pickerValue = (() => {
    try {
      return Color(effectiveHex as `#${string}`)
    } catch {
      return Color('#000000')
    }
  })()

  const handlePickerChange = (color: ColorValue, isHoverSelection?: boolean) => {
    // Drop alpha — moodboard color hex is 6-digit, no alpha channel.
    const hex = colorToHex({ ...color, a: 255 }).slice(0, 7)
    if (isHoverSelection) {
      setPreviewHex(hex)
      return
    }
    // Keep previewing the committed color until persist + refetch lands, then clear —
    // clearing the preview before the round-trip briefly flashes the old color.
    setPreviewHex(hex)
    void Promise.resolve(onChange({ hex })).finally(() =>
      // Only clear if a newer pick hasn't replaced our preview (rapid successive commits).
      setPreviewHex((cur) => (cur === hex ? null : cur)),
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: SNAP_CURVE }}
      className="group absolute"
      style={{
        left: position.x,
        top: position.y,
        width: size.w,
        height: size.h,
      }}>
      <div
        {...drag}
        onDoubleClick={() => setPickerOpen(true)}
        className={[
          'ring-border/60 relative h-full w-full overflow-hidden rounded-lg shadow-sm ring-1 transition-shadow',
          isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
          isSelected ? 'ring-primary ring-offset-background ring-2 ring-offset-2' : '',
        ].join(' ')}
        style={{ backgroundColor: effectiveHex }}>
        <ItemInfoBadge
          polarity={item.polarity}
          annotation={item.annotation}
          onChange={onMetaChange}>
          <p className="text-foreground font-medium">Brand color</p>
          <p className="mt-1">{effectiveHex}</p>
        </ItemInfoBadge>

        <div
          className={[
            'absolute inset-x-0 bottom-0 flex items-baseline justify-between gap-2 px-3 py-2 backdrop-blur-sm',
            overlayBg,
          ].join(' ')}>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                ref={pickerTriggerRef}
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                className={[
                  'cursor-pointer rounded text-[11px] font-medium uppercase tracking-wide outline-none',
                  overlayText,
                ].join(' ')}
                title="Click to change color">
                {effectiveHex}
              </button>
            </PopoverTrigger>
            <PopoverContent
              ref={pickerContentRef}
              align="start"
              side="top"
              sideOffset={8}
              className="w-auto p-3"
              onPointerDown={(e) => e.stopPropagation()}>
              <ColorPickerQuickmenu
                value={pickerValue}
                onChange={handlePickerChange}
                colorPalette={[]}
                showAlpha={false}
              />
            </PopoverContent>
          </Popover>

          <input
            type="text"
            value={labelDraft ?? item.label ?? ''}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                e.preventDefault()
                ;(e.currentTarget as HTMLInputElement).blur()
              }
              if (e.key === 'Escape') {
                setLabelDraft(null)
                ;(e.currentTarget as HTMLInputElement).blur()
              }
            }}
            placeholder="Label"
            className={[
              'min-w-0 max-w-[60%] truncate rounded bg-transparent text-right text-[11px] outline-none',
              overlayMuted,
              overlayPlaceholder,
              'focus:bg-background/20',
            ].join(' ')}
            aria-label="Label"
          />
        </div>

        <ResizeHandle itemId={item.id} baseSize={item.size} onCommit={onResize} />
      </div>
    </motion.div>
  )
}

// Standard relative luminance check — used to pick between light- and dark-text overlay.
// Threshold ~0.6 keeps mid-grays on the dark-text side.
function isLightColor(hex: string): boolean {
  try {
    const c = hexToColor(hex)
    const r = c.r / 255
    const g = c.g / 255
    const b = c.b / 255
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
    return lum > 0.6
  } catch {
    return false
  }
}
